import type { AccountId } from "../types";
import type { AccountSnapshot, PlatformId, PostMetric, Topic, TopicStatus } from "../types";
import { TOPIC_STATUSES } from "../types";
import { topicAccount } from "./accounts";
import { visibleRows } from "./merge";

export function activeTopics(topics: Topic[]): Topic[] {
  return topics.filter((topic) => !topic.inTrash);
}

export function topicsForAccount(topics: Topic[], accountId: AccountId): Topic[] {
  return activeTopics(topics).filter((topic) => topicAccount(topic) === accountId);
}

export function statusCounts(topics: Topic[]): Record<TopicStatus, number> {
  const counts = Object.fromEntries(TOPIC_STATUSES.map((status) => [status, 0])) as Record<TopicStatus, number>;
  for (const topic of activeTopics(topics)) counts[topic.status] += 1;
  return counts;
}

export interface BatchProgress {
  batch: string;
  total: number;
  done: number;
  rejected: number;
}

export function batchProgress(topics: Topic[]): BatchProgress[] {
  const groups = new Map<string, BatchProgress>();
  for (const topic of activeTopics(topics)) {
    const batch = (topic.batch ?? "").trim() || "未分批";
    const current = groups.get(batch) ?? { batch, total: 0, done: 0, rejected: 0 };
    if (topic.status === "不做") current.rejected += 1;
    else {
      current.total += 1;
      if (topic.status === "已完成") current.done += 1;
    }
    groups.set(batch, current);
  }
  return [...groups.values()].sort((a, b) => a.batch.localeCompare(b.batch, "zh"));
}

export function latestSnapshot(snapshots: AccountSnapshot[], platform: PlatformId): AccountSnapshot | null {
  const rows = visibleRows(snapshots).filter((item) => item.platform === platform);
  rows.sort((a, b) => b.recordedDate.localeCompare(a.recordedDate) || b.updatedAt.localeCompare(a.updatedAt));
  return rows[0] ?? null;
}

export function snapshotsForPlatform(snapshots: AccountSnapshot[], platform: PlatformId): AccountSnapshot[] {
  return visibleRows(snapshots)
    .filter((item) => item.platform === platform)
    .sort((a, b) => a.recordedDate.localeCompare(b.recordedDate));
}

export function visiblePosts(posts: PostMetric[]): PostMetric[] {
  return visibleRows(posts).sort((a, b) => b.recordedDate.localeCompare(a.recordedDate) || b.title.localeCompare(a.title, "zh"));
}

export function matchesQuery(topic: Topic, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return [topic.title, topic.angle, topic.batch, topic.audience, topic.sources, topic.reason, topic.sourceAssistant]
    .filter((value): value is string => typeof value === "string")
    .join("\n")
    .toLowerCase()
    .includes(needle);
}
